<template>
  <div class="post-edit-page brick">
    <div class="special-link-wrapper">
      <link-to-post
        v-if="post"
        :post="post"
        class="special-link back-nav-button"
      >
        <v-icon x-large>keyboard_backspace</v-icon>
      </link-to-post>
    </div>

    <h1>Редагування посту</h1>

    <loader v-if="!post" class="centered"></loader>
    <div v-else>
      <post-form
        v-if="post"
        :post.sync="post"
        :media.sync="media"
      ></post-form>

      <div class="buttons">
        <v-btn
          large
          outline
          @click="save"
          :disabled="saving"
          class="no-margin"
        >
          Зберегти
        </v-btn>
        <v-btn
          large
          flat
          @click="cancel"
          :disabled="saving"
          class="no-margin"
        >
          Скасувати
        </v-btn>
      </div>


      <v-dialog
        persistent
        v-model="saving"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="headline">Збереження...</span>
          </v-card-title>
          <v-card-text>
            <v-progress-linear
              :class="{ 'progress-hidden': !stepSavingProgress }"
              v-model="stepSavingProgress"></v-progress-linear>
            <v-progress-linear v-model="globalSavingProgress"></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>

import moment from 'moment';
import { randomString } from '@/services/random';
import deepCopy from 'deep-copy';

import LinkToPost from '@/components/LinkToPost';
import Loader from '@/components/Loader';
import PostForm from '@/components/PostForm';

import { NOT_FOUND } from '@/firebase/errorTypes';
import { getPost, savePost } from '@/firebase/repositories/posts';
import { getPostMedia, createMedia, updateMedia, deleteMedia } from '@/firebase/repositories/media';
import { upload, remove as deleteFile, cache } from '@/firebase/repositories/storage';
import { getPostRoute } from '@/router/helper';

const fileKeys = ['video', 'image', 'poster'];

export default {
  name: 'PostEditPage',
  components: {
    LinkToPost,
    Loader,
    PostForm,
  },
  data() {
    return {
      post: null,
      media: [],
      originalMedia: [],
      error: null,
      saving: false,
      globalSavingProgress: 0,
      stepSavingProgress: 0,
    };
  },
  computed: {
    postId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      Promise.all([getPost(this.postId), getPostMedia(this.postId)])
        .then(([post, media]) => {
          this.post = post;
          this.media = media;
          this.originalMedia = deepCopy(media);
        })
        .catch((error) => {
          if (error === NOT_FOUND) {
            this.$router.push({ name: 'notFound' });
          }

          this.error = 'Помилка завантаження посту.';
        });
    },
    save() {
      this.saving = true;

      this.post.edited = moment().valueOf();

      if (!this.post.storagePrefix) {
        this.post.storagePrefix = randomString(3);
      }

      const postStoragePrefix = this.post.storagePrefix;

      const steps = [
        savePost.bind(this, this.postId, this.post),
      ];

      this.originalMedia.forEach((original) => {
        if (!this.media.find(item => item.id === original.id)) {
          fileKeys.forEach((fileKey) => {
            if (original[fileKey] && original[fileKey].ref) {
              steps.push(this.removeMediaFile.bind(this, original[fileKey]));
            }
          });

          steps.push(deleteMedia.bind(null, original.id));
        }
      });

      this.media.forEach((item, index) => {
        let mediaChanged = false;

        const originalMedia = item.id && this.originalMedia
          .find(originalItem => originalItem.id === item.id);

        if (!item.storagePrefix) {
          // eslint-disable-next-line no-param-reassign
          item.storagePrefix = randomString(3);
          mediaChanged = true;
        }
        const mediaStoragePrefix = item.storagePrefix;

        if (item.image && item.image.input) {
          const ref = `${postStoragePrefix}-${mediaStoragePrefix}-${item.image.input.name}`;
          steps.push(this.uploadMediaFile.bind(this, item.image, ref));

          const thumbnailRef = `${postStoragePrefix}-${mediaStoragePrefix}-thumbnail-${item.image.input.name}`;
          steps.push(this.uploadMediaFileThumbnail.bind(this, item.image, thumbnailRef));
          mediaChanged = true;
        }

        if (item.image && originalMedia && item.image.comment !== originalMedia.image.comment) {
          mediaChanged = true;
        }

        if (item.video && item.video.input) {
          const ref = `${postStoragePrefix}-${mediaStoragePrefix}-${item.video.input.name}`;
          steps.push(this.uploadMediaFile.bind(this, item.video, ref));
          mediaChanged = true;
        }

        if (item.video && originalMedia && item.video.comment !== originalMedia.video.comment) {
          mediaChanged = true;
        }

        if (item.audio && item.audio.input) {
          const ref = `${postStoragePrefix}-${mediaStoragePrefix}-${item.audio.input.name}`;
          steps.push(this.uploadMediaFile.bind(this, item.audio, ref));
          mediaChanged = true;
        }

        if (item.audio && originalMedia && item.audio.comment !== originalMedia.audio.comment) {
          mediaChanged = true;
        }

        if (item.poster && item.poster.ref && item.poster.removed) {
          steps.push(this.removeMediaFile.bind(this, item.poster));
          mediaChanged = true;
        }

        if (item.poster && item.poster.input) {
          const ref = `${postStoragePrefix}-${mediaStoragePrefix}-poster-${item.poster.input.name}`;
          steps.push(this.uploadMediaFile.bind(this, item.poster, ref));

          const thumbnailRef = `${postStoragePrefix}-${mediaStoragePrefix}-poster-thumbnail-${item.poster.input.name}`;
          steps.push(this.uploadMediaFileThumbnail.bind(this, item.poster, thumbnailRef));
          mediaChanged = true;
        }

        if (!item.id) {
          steps.push(this.createMediaStep.bind(this, this.postId, item, index));
        } else if (mediaChanged || item.order !== index) {
          steps.push(this.updateMediaStep.bind(this, item, index));
        }
      });

      this.media.forEach((item) => {
        ['image', 'video', 'audio', 'poster'].forEach((fileKey) => {
          if (item[fileKey] && item[fileKey].ref) {
            steps.push(cache.bind(this, item[fileKey].ref));
          }
          if (item[fileKey] && item[fileKey].thumbnailRef) {
            steps.push(cache.bind(this, item[fileKey].thumbnailRef));
          }
        });
      });

      steps.push(this.savePostThumbnailStep.bind(this, this.postId, this.post, this.media));

      this.runSteps(steps)
        .then(() => {
          this.globalSavingProgress = 100;
          setTimeout(() => {
            this.$router.push(getPostRoute(this.post));
          }, 500);
        });
    },
    progressCallback(value1, value2) {
      this.globalSavingProgress = value1;
      this.stepSavingProgress = value2;
    },
    runSteps(steps) {
      const count = steps.length;
      let index = 0;
      let promise = Promise.resolve();

      while (steps.length) {
        const step = steps.shift();
        const cb = this.progressCallback.bind(this, Math.floor((index / count) * 100));

        promise = promise.then(() => {
          cb(0);
          return step(cb)
            .then(() => (this.stepSavingProgress && cb(100)));
        });

        index += 1;
      }

      return promise;
    },

    uploadMediaFile(fileInfo, ref, progressCallback) {
      return upload(ref, fileInfo.input, progressCallback)
        .then((publicUrl) => {
          /* eslint-disable no-param-reassign */
          fileInfo.ref = ref;
          fileInfo.url = publicUrl;
          delete fileInfo.input;
          /* eslint-enable no-param-reassign */
        });
    },
    uploadMediaFileThumbnail(fileInfo, ref, progressCallback) {
      return upload(ref, fileInfo.inputPreview, progressCallback)
        .then((publicUrl) => {
          /* eslint-disable no-param-reassign */
          fileInfo.thumbnailRef = ref;
          fileInfo.thumbnailUrl = publicUrl;
          delete fileInfo.inputPreview;
          /* eslint-enable no-param-reassign */
        });
    },
    removeMediaFile(fileInfo) {
      const promises = [deleteFile(fileInfo.ref)];
      if (fileInfo.thumbnailRef) {
        promises.push(deleteFile(fileInfo.thumbnailRef));
      }
      return Promise.all(promises)
        .then(() => {
          /* eslint-disable no-param-reassign */
          delete fileInfo.ref;
          delete fileInfo.url;
          delete fileInfo.thumbnailRef;
          delete fileInfo.thumbnailUrl;

          delete fileInfo.removed;
          /* eslint-enable no-param-reassign */
        });
    },
    createMediaStep(postId, media, index) {
      const data = {
        ...media,
        postId,
        order: index,
      };

      return createMedia(this.getClearMedia(data));
    },
    updateMediaStep(media, index) {
      return updateMedia(media.id, this.getClearMedia({ ...media, order: index }));
    },
    savePostThumbnailStep(postId, post, media) {
      let thumbnailUrl = null;
      const counters = {
        comment: post.counters.comment || null,
      };

      (media || []).forEach((item) => {
        counters[item.type] = counters[item.type] || 0;
        counters[item.type] += 1;

        if (!thumbnailUrl) {
          fileKeys.forEach((fileKey) => {
            if (item[fileKey] && item[fileKey].thumbnailUrl) {
              thumbnailUrl = item[fileKey].thumbnailUrl;
            }
          });
        }
      });

      // if (!thumbnailUrl || thumbnailUrl === this.post.thumbnailUrl) {
      //   return Promise.resolve();
      // }

      return savePost(postId, {
        ...post,
        thumbnailUrl,
        counters,
      });
    },
    getClearMedia(media) {
      const clear = { ...media };

      fileKeys.forEach((fileKey) => {
        if (clear[fileKey]) {
          delete clear[fileKey].input;
          delete clear[fileKey].inputPreview;
        }
      });

      return clear;
    },
    cancel() {
      this.$router.push(getPostRoute(this.post));
    },
  },
};

</script>

<style scoped>

.buttons {
  margin-top: 20px;
}

.progress-hidden {
  visibility: hidden;
}

</style>



// WEBPACK FOOTER //
// src/containers/PostEditPage.vue